import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Download,
  Clock,
  Users,
  Package,
  Award,
  CheckCircle2,
  BookOpen,
  ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import { KITS, getKit } from "../data/kits";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const KitDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const kit = getKit(slug);
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!kit?.productSlug) return;
    api
      .get(`/products/${kit.productSlug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null));
  }, [kit?.productSlug]);

  if (!kit) return <Navigate to="/steam" replace />;

  const buyKit = (goToCart = false) => {
    if (!product) {
      toast.error("Kit unavailable right now — please refresh.");
      return;
    }
    setAdding(true);
    addItem(product, { quantity: 1 });
    toast.success(`Added "${product.name}" to cart`);
    setAdding(false);
    if (goToCart) navigate("/cart");
  };

  const idx = KITS.findIndex((k) => k.slug === kit.slug);
  const prev = idx > 0 ? KITS[idx - 1] : KITS[KITS.length - 1];
  const next = idx < KITS.length - 1 ? KITS[idx + 1] : KITS[0];

  return (
    <main data-testid="kit-detail-page" className="bg-white">
      {/* Breadcrumb */}
      <div className="border-b border-black/10">
        <div className="container-x py-4 flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-black text-black/70">
          <Link to="/steam" className="hover:text-[#EC008C]">STEAM Kits</Link>
          <ChevronRight size={12} />
          <span className="text-[#EC008C]">Kit {String(kit.order).padStart(2, "0")}</span>
        </div>
      </div>

      {/* Hero */}
      <section
        data-testid="kit-hero"
        className="relative overflow-hidden"
        style={{ background: kit.tint, color: kit.tintText === "white" ? "#fff" : "#000" }}
      >
        <div className="container-x py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div
              className="inline-flex items-center gap-2 border-2 px-3 py-1.5 text-[11px] tracking-[0.28em] uppercase font-black mb-6"
              style={{ borderColor: kit.tintText === "white" ? "#fff" : "#000" }}
            >
              <BookOpen size={12} /> Kit {String(kit.order).padStart(2, "0")} · {kit.tag}
            </div>
            <h1
              className="font-display font-black text-4xl md:text-6xl leading-[1.02] tracking-tight"
              style={{ color: kit.tintText === "white" ? "#fff" : "#000" }}
            >
              {kit.title}
            </h1>
            <div className="mt-4 font-script text-2xl md:text-3xl" style={{ color: kit.tintText === "white" ? "#F3E30A" : "#EC008C" }}>
              &ldquo;{kit.tagline}&rdquo;
            </div>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed font-light" style={{ opacity: 0.9 }}>
              {kit.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => buyKit(true)}
                disabled={!product || adding}
                data-testid="kit-cta-shop"
                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-black hover:bg-white hover:text-black transition-colors disabled:opacity-60"
              >
                <ShoppingBag size={14} />
                {product ? `Buy This Kit · $${Number(product.price).toFixed(2)}` : "Loading kit…"}
                <ArrowRight size={14} />
              </button>
              <button
                type="button"
                onClick={() => buyKit(false)}
                disabled={!product || adding}
                data-testid="kit-cta-add"
                className="inline-flex items-center gap-2 bg-transparent px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 transition-colors hover:bg-black hover:text-white hover:border-black disabled:opacity-60"
                style={{ borderColor: kit.tintText === "white" ? "#fff" : "#000", color: kit.tintText === "white" ? "#fff" : "#000" }}
              >
                Add to Cart
              </button>
              <a
                href="/steam/steam-2-go-grant-catalog.pdf"
                download="MAKEBA-STEAM-2-Go-Grant-Catalog.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="kit-cta-catalog"
                className="inline-flex items-center gap-2 bg-transparent px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 transition-colors hover:bg-black hover:text-white hover:border-black"
                style={{ borderColor: kit.tintText === "white" ? "#fff" : "#000", color: kit.tintText === "white" ? "#fff" : "#000" }}
              >
                <Download size={14} /> Grant Catalog PDF
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative border-2 border-black overflow-hidden aspect-[4/5] bg-white">
              <img src={kit.photos[0].src} alt={kit.photos[0].label} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/85 text-white text-[10px] tracking-[0.28em] uppercase font-black px-4 py-3">
                {kit.photos[0].label}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section data-testid="kit-facts" className="bg-black text-white border-y-2 border-black">
        <div className="container-x grid grid-cols-2 md:grid-cols-4">
          <div className="p-6 md:p-8 border-r border-white/15">
            <Users size={20} className="mb-3 text-[#DCE842]" strokeWidth={2.5} />
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-white/60 mb-1">Ages</div>
            <div className="font-display font-black text-xl text-white">{kit.ageRange}</div>
          </div>
          <div className="p-6 md:p-8 border-r border-white/15">
            <Clock size={20} className="mb-3 text-[#DCE842]" strokeWidth={2.5} />
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-white/60 mb-1">Duration</div>
            <div className="font-display font-black text-xl text-white">{kit.duration}</div>
          </div>
          <div className="p-6 md:p-8 border-r border-white/15">
            <Package size={20} className="mb-3 text-[#DCE842]" strokeWidth={2.5} />
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-white/60 mb-1">Format</div>
            <div className="font-display font-black text-xl text-white">100% Screen-Free</div>
          </div>
          <div className="p-6 md:p-8">
            <Award size={20} className="mb-3 text-[#DCE842]" strokeWidth={2.5} />
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-white/60 mb-1">Funding</div>
            <div className="font-display font-black text-xl text-white">Grant Eligible</div>
          </div>
        </div>
      </section>

      {/* Activities */}
      <section data-testid="kit-activities" className="container-x py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">Session Plan</div>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-black tracking-tight">
            What students actually do.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {kit.activities.map((a, i) => (
            <div key={i} className="border-2 border-black p-6 md:p-8 bg-white flex gap-5 hover:bg-[#FAFAF4] transition-colors">
              <div
                className="w-12 h-12 shrink-0 rounded-full border-2 border-black flex items-center justify-center font-display font-black text-lg"
                style={{ background: kit.tint, color: kit.tintText === "white" ? "#fff" : "#000" }}
              >
                {i + 1}
              </div>
              <div>
                <h3 className="font-display font-black text-xl text-black leading-tight">{a.title}</h3>
                <p className="mt-2 text-sm md:text-base text-[#3A3A3A] leading-relaxed font-light">{a.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Materials + Standards */}
      <section data-testid="kit-materials-standards" className="bg-[#FAFAF4] border-y-2 border-black">
        <div className="container-x py-20 md:py-28 grid md:grid-cols-12 gap-12">
          {/* Materials */}
          <div className="md:col-span-6">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">What&rsquo;s Inside The Box</div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-black tracking-tight mb-8">
              Full materials list.
            </h2>
            <ul className="space-y-3">
              {kit.materials.map((m, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="text-[#EC008C] shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-base text-black font-light">{m}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Standards */}
          <div className="md:col-span-6">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">Standards Alignment</div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-black tracking-tight mb-8">
              NGSS & CCSS aligned.
            </h2>
            <div className="space-y-4">
              {kit.standards.map((st, i) => (
                <div key={i} className="border-l-4 border-[#EC008C] pl-5 py-1.5">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#EC008C] mb-1">{st.code}</div>
                  <div className="text-sm md:text-base text-black leading-snug font-light">{st.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section data-testid="kit-outcomes" className="container-x py-20 md:py-28">
        <div className="max-w-2xl mb-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">Learning Outcomes</div>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-black tracking-tight">
            By the last session, every student can:
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-4 max-w-4xl">
          {kit.outcomes.map((o, i) => (
            <div key={i} className="flex gap-4 items-start border-t border-black/10 pt-4">
              <div className="text-[#EC008C] font-display font-black text-2xl leading-none w-8 shrink-0">0{i + 1}</div>
              <div className="text-base md:text-lg text-black font-light leading-snug">{o}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trailblazers */}
      <section data-testid="kit-trailblazers" className="bg-black text-white py-20 md:py-28">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#DCE842] font-black mb-4">Featured Trailblazers</div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-white tracking-tight">
              Kids meet the real scientists behind the work.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {kit.trailblazers.map((t, i) => (
              <div key={i} className="border-2 border-[#DCE842] p-6 md:p-8">
                <div
                  className="w-14 h-14 rounded-full border-2 border-[#DCE842] flex items-center justify-center font-display font-black text-xl text-[#DCE842] mb-5"
                >
                  {t.name
                    .replace(/^(Dr\.|Ms\.|Mr\.)\s/, "")
                    .split(" ")
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")}
                </div>
                <h3 className="font-display font-black text-2xl text-white">{t.name}</h3>
                <p className="mt-3 text-base text-white/80 font-light leading-relaxed">{t.credit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Classroom photos */}
      <section data-testid="kit-photos" className="container-x py-20 md:py-28">
        <div className="max-w-2xl mb-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">In The Wild</div>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-black tracking-tight">
            From our workshop to classrooms nationwide.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kit.photos.map((g, i) => (
            <div key={i} className="relative aspect-[4/5] overflow-hidden border-2 border-black group">
              <img src={g.src} alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-[10px] tracking-[0.24em] uppercase font-black px-3 py-2">
                {g.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Big CTA */}
      <section data-testid="kit-cta" className="bg-[#EC008C] text-white border-y-2 border-black">
        <div className="container-x py-20 md:py-28 max-w-4xl">
          <h2 className="font-display font-black text-4xl md:text-6xl leading-[1.02] text-white tracking-tight">
            Ready to bring {kit.title.split(",")[0]} to your classroom?
          </h2>
          <p className="mt-6 max-w-xl text-lg md:text-xl font-light text-white/90 leading-relaxed">
            Please allow several business days for your kits to be prepared for shipping — custom and made-to-order kits may require additional production time. District quotes and grant documentation are prepared within 1–2 business days.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => buyKit(true)}
              disabled={!product || adding}
              data-testid="kit-final-cta-shop"
              className="inline-flex items-center gap-2 bg-white text-[#EC008C] px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-black hover:text-white hover:border-black transition-colors disabled:opacity-60"
            >
              <ShoppingBag size={14} />
              {product ? `Buy This Kit · $${Number(product.price).toFixed(2)}` : "Loading…"}
              <ArrowRight size={14} />
            </button>
            <Link
              to="/shop?category=steam-kits"
              data-testid="kit-final-cta-all"
              className="inline-flex items-center gap-2 bg-transparent text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-white hover:text-[#EC008C] transition-colors"
            >
              See All STEAM Kits <ArrowRight size={14} />
            </Link>
            <a
              href="/steam/steam-2-go-grant-catalog.pdf"
              download="MAKEBA-STEAM-2-Go-Grant-Catalog.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="kit-final-cta-catalog"
              className="inline-flex items-center gap-2 bg-transparent text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-white hover:text-[#EC008C] transition-colors"
            >
              <Download size={14} /> Download Grant Catalog
            </a>
          </div>
        </div>
      </section>

      {/* Prev / Next */}
      <section data-testid="kit-nav" className="container-x py-14 grid md:grid-cols-2 gap-4">
        <Link
          to={`/steam/${prev.slug}`}
          className="group border-2 border-black p-6 md:p-8 flex items-start gap-5 hover:bg-black hover:text-white transition-colors"
        >
          <ArrowLeft size={22} className="shrink-0 mt-1" />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black opacity-60 mb-2">Previous kit</div>
            <div className="font-display font-black text-2xl leading-tight">{prev.title}</div>
          </div>
        </Link>
        <Link
          to={`/steam/${next.slug}`}
          className="group border-2 border-black p-6 md:p-8 flex items-start gap-5 justify-end text-right hover:bg-black hover:text-white transition-colors"
        >
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black opacity-60 mb-2">Next kit</div>
            <div className="font-display font-black text-2xl leading-tight">{next.title}</div>
          </div>
          <ArrowRight size={22} className="shrink-0 mt-1" />
        </Link>
      </section>
    </main>
  );
};

export default KitDetail;
